import { IonImg, IonProgressBar, useIonToast } from '@ionic/react';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { useWizard } from '../../hooks/WizardHooks';
import { getWizardStep } from './WizardSteps';
import './WizardCss.css';
import { useLogger } from '../../services/LogService';
import { PreviousIcon } from './PreviousIcon';
import { Step } from '../../model/wizard/wizard';

type Logo = {
  src: string;
  alt: string;
};

type Bridge = {
  src: string;
  alt: string;
};

const logo: Logo = {
  src: '/assets/logo/logoPortail.svg',
  alt: 'Logo CIUP',
};

const bridge: Bridge = {
  src: '/assets/img/bridge.svg',
  alt: 'Bridge logo CIUP',
};

interface WizardContentProps {
  currentStep: string;
  user: any;
  parameters: any;
  updateParameter: any;
}

export const WizardContent: React.FC<WizardContentProps> = ({
  currentStep,
  user,
  parameters,
  updateParameter,
}) => {
  const NB_MIN_INTERESTS = 3;

  const [isValid, setValid] = useState(false);

  const logger = useLogger('WizardContent');

  const intl = useIntl();
  const [present] = useIonToast();

  const {
    getNextStep,
    saveStep,
    getPreviousStep,
    isFirstStep,
    isLastStep,
    getStepIndex,
    WIZARDSTEPS,
    loading,
    isSetLoading,
  } = useWizard();

  const currentStepWizard = user
    ? getWizardStep(currentStep as Step, user)
    : undefined;

  const failed = (msgId: string) => {
    present({
      color: 'dark',
      position: 'top',
      message: intl.formatMessage({ id: msgId }),
      duration: 3000,
    });
  }; // TODO : Faire un hook pour le failed et le success pour wizard modal et directory modal, partout ou on utilise les toasts

  const handleClick = async () => {
    if (nextStepSlug !== '/wizard/done') {
      isSetLoading(true);
    }

    if (currentStepWizard && currentStepWizard.slug !== 'done') {
      let canSave = true;
      if ((currentStep as Step) === 'photo') {
        if (!parameters.photo || parameters.photo === '') {
          canSave = false;
          failed('No picture');
        }
      }

      if ((currentStep as Step) === 'cursus') {
        if (
          !parameters.establishment ||
          !parameters.studyYear ||
          !parameters.discipline
        ) {
          canSave = false;
          failed('No cursus');
        }
      }

      if ((currentStep as Step) === 'interests') {
        if (parameters.interest.length < 3) {
          canSave = false;
          failed('Too few interests');
        }
      }

      if (canSave) {
        const save = await saveStep(currentStep);
        if (!save) {
          failed('Failed save data');
        }
        return save;
      } else {
        return false;
      }
    }
  };

  const handleClickBackward = () => {
    isSetLoading(true);
    return true;
  };

  const nextStepSlug = getNextStep(currentStep)
    ? '/wizard/' + getNextStep(currentStep)
    : '';

  useEffect(() => {
    logger.info(
      `useEffect(parameters) current step: ${currentStep}`,
      parameters
    );
    switch (currentStep as Step) {
      case 'language':
        setValid(true);
        break;
      case 'photo':
        const photoValid =
          parameters.photo && parameters.photo !== '' ? true : false;
        setValid(photoValid);
        break;
      case 'cursus':
        const validCursus =
          parameters.establishment &&
          parameters.studyYear &&
          parameters.discipline
            ? true
            : false;
        setValid(validCursus);
        break;
      case 'notifications':
        setValid(true);
        break;
      case 'annuaire':
        setValid(true);
        break;
      case 'interests':
        const validInterest =
          parameters.interest.length >= NB_MIN_INTERESTS ? true : false;
        setValid(validInterest);
        break;
      case 'done':
        setValid(true);
        break;
      default:
        setValid(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameters, currentStep]);

  if (currentStepWizard && user) {
    const nextText = isFirstStep(currentStep) ? (
      <FormattedMessage id="Get started !" />
    ) : isLastStep(currentStep) ? (
      <FormattedMessage id="Let's go !" />
    ) : (
      <FormattedMessage id="Next" />
    );

    return (
      <div className="wizard grid grid-cols-1 md:grid-cols-2 w-full h-full bg-white">
        <div className="">
          <IonImg
            slot="start"
            className="mt-9 mb-10 w-48 md:absolute md:left-8 md:top-3 md:w-44 hidden md:block"
            alt={logo.alt}
            src={logo.src}
          />
          <div className="flex justify-between items-center px-4 my-8  md:hidden w-full">
            {!isFirstStep(currentStep) && (
              <div className="mt-safe-area flex justify-center items-center w-8 h-8 p-3 rounded-full bg-primary">
                <Link
                  to={'/wizard/' + getPreviousStep(currentStep)}
                  className="flex justify-center items-center w-10 h-10 rounded-full"
                  onClick={() => {
                    handleClickBackward();
                  }}
                >
                  <PreviousIcon className="text-white" />
                </Link>
              </div>
            )}
            <IonProgressBar
              className="mt-safe-area wizard-content-mobile mx-4 h-2 rounded-2xl"
              value={(getStepIndex(currentStep) + 1) / WIZARDSTEPS.length}
            ></IonProgressBar>
          </div>
          <div className="flex flex-col justify-start items-center relative top-0 md:top-32 md:justify-center">
            <div
              className={`${
                !loading && isValid ? 'is-valid' : ''
              } scrollable scroll-y`}
            >
              <div className="px-4 w-full md:w-3/4 md:mx-auto items-center justify-center">
                <h1 className="text-center text-black font-bold text-xl md:text-4xl">
                  {currentStepWizard?.title}
                </h1>
                <div className="flex flex-col text-black justify-center items-center my-5">
                  {currentStepWizard?.description}
                </div>
                <div className="w-full mt-4 md:mt-10">
                  {currentStepWizard.component !== null && (
                    <>
                      {React.createElement(currentStepWizard?.component, {
                        updateParameter: updateParameter,
                        user: user,
                        parameters: parameters,
                        isSetLoading: isSetLoading,
                      })}
                    </>
                  )}
                </div>
              </div>
            </div>
            {!loading && isValid && (
              <div
                className={`mb-safe-area flex justify-center items-center w-full mt-11 fixed bottom-0 left-0 md:relative bg-white py-4`}
              >
                <Link
                  to={!loading && isValid ? nextStepSlug : currentStep}
                  className={`w-80 text-center text-white font-bold text-sm rounded-full py-4 ${
                    !loading && isValid
                      ? 'bg-primary'
                      : 'bg-lightGrey cursor-not-allowed'
                  }`}
                  onClick={() => {
                    handleClick();
                  }}
                >
                  {nextText}
                </Link>
              </div>
            )}
          </div>
        </div>
        <div className="relative bg-primary hidden md:block">
          <div className="flex items-center justify-center md:absolute md:w-4/5 md:left-1/2 md:transform md:-translate-x-1/2 md:top-16">
            {!isFirstStep(currentStep) && (
              <div className="flex justify-center items-center w-8 h-8 p-3 rounded-full bg-white">
                <Link
                  to={'/wizard/' + getPreviousStep(currentStep)}
                  className="flex justify-center items-center w-10 h-10 rounded-full"
                  onClick={() => {
                    handleClickBackward();
                  }}
                >
                  <PreviousIcon className="text-primary" />
                </Link>
              </div>
            )}
            <IonProgressBar
              className="wizard-content mx-4 h-3 rounded-2xl"
              value={(getStepIndex(currentStep) + 1) / WIZARDSTEPS.length}
            ></IonProgressBar>
          </div>
          <div className="flex justify-end h-full">
            <img
              className="object-contain w-11/12 xl:w-9/12 2xl:w-7/12 absolute top-1/2 transform -translate-y-1/2 z-10"
              src={currentStepWizard.image}
              alt="Chargement impossible"
            />
            <IonImg
              slot="start"
              className="hidden md:block md:absolute md:left-0 md:bottom-0 md:w-2/3 "
              alt={bridge.alt}
              src={bridge.src}
            />
          </div>
        </div>
      </div>
    );
  } else {
    return <>No data</>;
  }
};
